import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useDebounce from "../../../../../../hooks/useDebounce";
import {
  getCities,
  getCountries,
  getLookups,
} from "../../../../../../network/apis/lookups";
import { getAdminEventRequestsRequest } from "../../../../../../store/AdminEventRequests/actions";
import { lookupsKeys } from "../../../../../../utils/Constants";
import {
  getCountryOrderParams,
  normalizeCountryOptions,
} from "../../../../../../utils/countryOptions";
import { showErrorMsg } from "../../../../../../utils/Helpers";
import { consumeEventApplicantsListState } from "../../../../../../utils/companyPortal/eventApplicantsListState";
import { buildAdminEventRequestListParams } from "../../adminEventRequestMappers";
import {
  getRatingFilterOptions,
  getRoleOptionsFromEvent,
  getShiftOptionsFromEvent,
  getZoneOptionsFromEvent,
  normalizeEventRequest,
  normalizeRoleLookupOptions,
  readEventCompanyId,
  resolveCheckedInSummary,
  summaryMatchesEvent,
} from "../../eventRequestMappers";
import { getZoneApprovedRequestsCount } from "../../../Zones/ZoneItem/zoneStaffing";
import { joiningRequestStatuses } from "../../constants";

const SEARCH_DEBOUNCE_MS = 450;
const COUNTRY_SEARCH_MIN_LENGTH = 1;

const readZoneApplicantsCount = (zone) => {
  const attrs = zone?.attributes || zone;
  const count = getZoneApprovedRequestsCount(attrs);

  return count > 0 ? count : null;
};

const extractLookupArray = (response, key) => {
  const data = response?.data?.data;
  // `/lookups` returns an array of single-key buckets, e.g. [{ roles: [...] }, { tags: [...] }]
  if (Array.isArray(data)) {
    for (const bucket of data) {
      if (bucket && typeof bucket === "object" && Array.isArray(bucket[key])) {
        return bucket[key];
      }
    }
    return [];
  }
  if (Array.isArray(data?.[key])) return data[key];
  return [];
};

const useAdminApplicantsList = ({
  event,
  lang,
  sharedLabels,
  initialStatus = joiningRequestStatuses.pending,
  enabledStatuses,
  enabled = true,
}) => {
  const dispatch = useDispatch();
  const storeRows = useSelector((state) => state.adminEventRequests.list);
  const meta = useSelector((state) => state.adminEventRequests.meta);
  const summary = useSelector((state) => state.adminEventRequests.summary);

  const restoredStateRef = useRef(
    event?.id ? consumeEventApplicantsListState(event.id, "admin") : null,
  );
  const pendingScrollRef = useRef(restoredStateRef.current?.scrollY ?? null);
  const restored = restoredStateRef.current;

  const [activeStatus, setActiveStatus] = useState(
    () => restored?.activeStatus ?? initialStatus,
  );
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState(() => restored?.search ?? "");
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const [filters, setFilters] = useState(
    () =>
      restored?.filters ?? {
        workingHours: null,
        requestDate: null,
        nationality: null,
        requestedRole: null,
        shift: null,
        zone: null,
        rating: null,
        checkIn: null,
        allocationStatus: null,
        applicantType: null,
        gender: null,
        city: null,
      },
  );
  const [page, setPage] = useState(() => restored?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => restored?.pageSize ?? 10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRowsById, setSelectedRowsById] = useState({});
  const [nationalityOptions, setNationalityOptions] = useState([]);
  const [nationalitySearch, setNationalitySearch] = useState("");
  const debouncedNationalitySearch = useDebounce(
    nationalitySearch,
    SEARCH_DEBOUNCE_MS,
  );
  const [nationalityLoading, setNationalityLoading] = useState(false);
  const [tagOptions, setTagOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const companyId = readEventCompanyId(event);

  const eventShiftOptions = useMemo(
    () => getShiftOptionsFromEvent(event, lang, sharedLabels),
    [event, lang, sharedLabels],
  );
  const eventRoleOptions = useMemo(
    () => getRoleOptionsFromEvent(event, lang),
    [event, lang],
  );
  const eventZoneOptions = useMemo(
    () => getZoneOptionsFromEvent(event, lang),
    [event, lang],
  );
  const allocateZoneOptions = useMemo(() => {
    const zones = event?.zones ?? event?.zones_attributes ?? [];
    return (Array.isArray(zones) ? zones : [])
      .map((zone) => {
        const attrs = zone?.attributes || zone;
        const supervisor = attrs.supervisor || {};
        const supervisorId = supervisor.id ?? attrs.supervisor_id;
        const supervisorName =
          (lang === "ar" ? supervisor.full_name_ar : supervisor.full_name_en) ||
          [
            supervisor.first_name,
            supervisor.middle_name,
            supervisor.last_name,
          ]
            .filter(Boolean)
            .join(" ");

        return {
          id: attrs.id,
          name:
            (lang === "ar" ? attrs.name_ar : attrs.name_en) ||
            attrs.name ||
            "",
          supervisorId,
          supervisorName,
          applicantsInZone: readZoneApplicantsCount(zone),
        };
      })
      .filter((option) => option.id && option.name);
  }, [event, lang]);
  const ratingOptions = useMemo(() => getRatingFilterOptions(), []);
  const shiftFilterOptions = useMemo(
    () =>
      eventShiftOptions.map((shift) => ({
        id: shift.id,
        name: shift.name,
      })),
    [eventShiftOptions],
  );
  const listParams = useMemo(
    () =>
      buildAdminEventRequestListParams({
        eventId: event?.id,
        event,
        activeStatus,
        search: debouncedSearch,
        filters,
        page,
        pageSize,
      }),
    [activeStatus, companyId, debouncedSearch, event?.id, filters, page, pageSize],
  );
  const localizedRows = useMemo(
    () =>
      rows.map((row) => {
        if (!row?.raw) return row;
        const next = normalizeEventRequest(row.raw, lang, sharedLabels);
        return { ...next, userTag: row.userTag ?? next.userTag ?? null };
      }),
    [rows, lang, sharedLabels],
  );
  const selectedApplicants = useMemo(() => {
    const currentRowsById = Object.fromEntries(
      localizedRows.map((row) => [String(row.id), row]),
    );

    return selectedIds
      .map((id) => currentRowsById[String(id)] || selectedRowsById[String(id)])
      .filter(Boolean);
  }, [localizedRows, selectedIds, selectedRowsById]);

  const statusSummary = useMemo(() => {
    const hasSummary = summaryMatchesEvent(summary, event);
    const base = {
      [joiningRequestStatuses.pending]: hasSummary ? (summary.pending ?? 0) : 0,
      [joiningRequestStatuses.joined]: hasSummary ? (summary.joined ?? 0) : 0,
      [joiningRequestStatuses.rejected]: hasSummary ? (summary.rejected ?? 0) : 0,
      [joiningRequestStatuses.removed]: hasSummary ? (summary.removed ?? 0) : 0,
    };
    if (!enabledStatuses?.length) return base;
    return Object.fromEntries(
      enabledStatuses.map((status) => [status, base[status] ?? 0]),
    );
  }, [enabledStatuses, event, summary]);
  const { checkedIn: checkedInCount, notCheckedIn: notCheckedInCount } = useMemo(
    () => resolveCheckedInSummary(summary, event),
    [event, summary],
  );

  useEffect(() => {
    setRows(storeRows);
  }, [storeRows]);

  useEffect(() => {
    if (pendingScrollRef.current == null || !rows.length) return undefined;

    const scrollY = pendingScrollRef.current;
    pendingScrollRef.current = null;
    const frameId = requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });

    return () => cancelAnimationFrame(frameId);
  }, [rows.length]);

  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;
    const query = debouncedNationalitySearch.trim();
    const selectedNationalityName = filters.nationality?.name || "";
    const isSelectedNationalitySearch =
      selectedNationalityName &&
      query.toLowerCase() === selectedNationalityName.toLowerCase();
    const shouldSearchCountries =
      !query || query.length >= COUNTRY_SEARCH_MIN_LENGTH;

    if (isSelectedNationalitySearch || !shouldSearchCountries) {
      return undefined;
    }

    setNationalityLoading(true);
    getCountries(
      {
        page: -1,
        ...getCountryOrderParams(lang),
        ...(query ? { name: query } : {}),
      },
      { handlerEnabled: false },
    )
      .then((response) => {
        if (mounted) {
          setNationalityOptions(
            normalizeCountryOptions(response.data?.data, lang),
          );
        }
      })
      .catch((error) => {
        showErrorMsg(error.response?.data?.message);
      })
      .finally(() => {
        if (mounted) {
          setNationalityLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [debouncedNationalitySearch, enabled, filters.nationality, lang]);

  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;

    getLookups([lookupsKeys.tags])
      .then((response) => {
        if (!mounted) return;
        setTagOptions(
          normalizeRoleLookupOptions(
            extractLookupArray(response, lookupsKeys.tags),
            lang,
          ),
        );
      })
      .catch((error) => {
        if (mounted) {
          showErrorMsg(error.response?.data?.message);
          setTagOptions([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [enabled, lang]);

  useEffect(() => {
    if (!enabled) return undefined;

    let mounted = true;

    getCities({ page: -1, ...getCountryOrderParams(lang) })
      .then((response) => {
        if (!mounted) return;
        setCityOptions(normalizeRoleLookupOptions(response.data?.data, lang));
      })
      .catch((error) => {
        if (mounted) {
          showErrorMsg(error.response?.data?.message);
          setCityOptions([]);
        }
      });

    return () => {
      mounted = false;
    };
  }, [enabled, lang]);

  useEffect(() => {
    if (!enabled || !event?.id) return;
    dispatch(getAdminEventRequestsRequest(listParams));
  }, [dispatch, enabled, event?.id, lang, listParams]);

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectedRowsById({});
  };

  const handleStatusChange = (nextStatus) => {
    if (enabledStatuses?.length && !enabledStatuses.includes(nextStatus)) {
      return;
    }
    setActiveStatus(nextStatus);
    setPage(1);
    clearSelection();
    if (nextStatus !== joiningRequestStatuses.joined) {
      setFilters((current) => ({
        ...current,
        zone: null,
        allocationStatus: null,
      }));
    }
  };

  const handleSelectRow = (id, checked) => {
    const selectedRow = localizedRows.find((row) => row.id === id);

    setSelectedIds((current) =>
      checked
        ? Array.from(new Set([...current, id]))
        : current.filter((item) => item !== id),
    );
    setSelectedRowsById((current) => {
      if (!checked) {
        const next = { ...current };
        delete next[String(id)];
        return next;
      }

      return selectedRow
        ? { ...current, [String(id)]: selectedRow }
        : current;
    });
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    clearSelection();
  };

  const handleFilterChange = (name, value) => {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
    clearSelection();
  };

  const handleNationalityOpen = () => {
    setNationalityLoading(true);
    getCountries(
      { page: -1, ...getCountryOrderParams(lang) },
      { handlerEnabled: false },
    )
      .then((response) => {
        setNationalityOptions(
          normalizeCountryOptions(response.data?.data, lang),
        );
      })
      .catch((error) => {
        showErrorMsg(error.response?.data?.message);
      })
      .finally(() => {
        setNationalityLoading(false);
      });
  };

  const handlePageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const handleSelectCurrentPage = (checked) => {
    const currentPageIds = localizedRows.map((row) => row.id);
    setSelectedIds((current) => {
      if (checked) {
        return Array.from(new Set([...current, ...currentPageIds]));
      }

      return current.filter((id) => !currentPageIds.includes(id));
    });
    setSelectedRowsById((current) => {
      if (checked) {
        return {
          ...current,
          ...Object.fromEntries(localizedRows.map((row) => [String(row.id), row])),
        };
      }

      const next = { ...current };
      currentPageIds.forEach((id) => {
        delete next[String(id)];
      });
      return next;
    });
  };

  return {
    activeStatus,
    allocateZoneOptions,
    checkedInCount,
    cityOptions,
    eventRoleOptions,
    eventShiftOptions,
    eventZoneOptions,
    filters,
    listParams,
    localizedRows,
    loyalApplicantsCount: summaryMatchesEvent(summary, event)
      ? (summary.loyalApplicantsCount ?? 0)
      : 0,
    meta,
    nationalityLoading,
    nationalityOptions,
    nationalitySearch,
    nonLoyalApplicantsCount: summaryMatchesEvent(summary, event)
      ? (summary.nonLoyalApplicantsCount ?? 0)
      : 0,
    notCheckedInCount,
    page,
    pageSize,
    ratingOptions,
    tagOptions,
    rows,
    search,
    selectedApplicants,
    selectedIds,
    setNationalitySearch,
    setPage,
    setRows,
    setSelectedIds,
    setSelectedRowsById,
    shiftFilterOptions,
    summary: statusSummary,
    handleFilterChange,
    handleNationalityOpen,
    handlePageSizeChange,
    handleSearchChange,
    handleSelectCurrentPage,
    handleSelectRow,
    handleStatusChange,
  };
};

export default useAdminApplicantsList;
